import { TeamMembersUrl, TeamMergeConfigUrl } from 'constants/RouteUrls'
import Button from 'core/components/buttons/Button'
import Modal from 'core/components/dialogs/Modal'
import Icon from 'core/components/icons/Icon'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import useMergePublic from 'core/hooks/integrations/merge/useMergePublic'
import useModal from 'core/hooks/ui/useModal'
import useNavigation from 'core/hooks/navigation/useNavigation'
import useNotifications from 'core/hooks/ui/useNotifications'
import { useSettings } from 'core/hooks/settings/useSettings'
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'

const MergeSuccessModal = () => {
  const { open: show, openModal, closeModal } = useModal()
  const notifications = useNotifications()
  const {
    data: { pollingJobs },
    set
  } = useSettings()
  const { data: currentTeam } = useCurrentTeam()
  const { mappedIntegrations } = useMergePublic()
  const { push } = useNavigation()
  const [recentIntegration, setRecentIntegration] = useState('')
  const [groupIntegrationId, setGroupIntegrationId] = useLocalStorage(
    'group_integration_id',
    ''
  )
  const queryClient = useQueryClient()
  const teamId = currentTeam?.token_id ? currentTeam.token_id : undefined

  const closeSuccessModal = useCallback(() => {
    closeModal()
    setRecentIntegration('')
  }, [closeModal])

  const reset = useCallback(() => {
    setGroupIntegrationId('')
    set('pollingJobs', {
      ...pollingJobs,
      mergeGroupIntegrationId: ''
    })
  }, [pollingJobs, set, setGroupIntegrationId])

  useQueryCallbacks({
    queryKey: ['merge', 'group-integrations-status'],
    queryFn: () =>
      APIService.integrations.getMergeGroupIntegration(
        String(teamId),
        groupIntegrationId
      ),
    enabled: !!teamId && !!groupIntegrationId,
    refetchInterval: 5000,
    onSuccess: (groupIntegration) => {
      queryClient.invalidateQueries({
        queryKey: ['merge', 'group-integrations']
      })
      const slug = groupIntegration.slug as string
      const name = mappedIntegrations?.[slug]?.name ?? slug
      if (groupIntegration.synced_status === 'done') {
        reset()
        openModal()
        setRecentIntegration(name)
      } else if (groupIntegration.status === 'Needs Configuration') {
        reset()
        notifications.error({
          id: 'relink-system',
          title: `${name} needs configuration`,
          subtitle:
            'Your HR system is linked but not syncing new members until configuration is complete',
          footer: () => (
            <div className="flex justify-end">
              <Button
                variant="primary"
                onClick={() => {
                  push(
                    TeamMergeConfigUrl.replaceParams({
                      token_id: String(currentTeam?.token_id),
                      id: String(groupIntegration.token_id)
                    })
                  )
                  notifications.dismiss('relink-system')
                }}
              >
                Configure
              </Button>
            </div>
          )
        })
      }
    },
    onError: () => {
      reset()
    }
  })

  useEffect(() => {
    if (pollingJobs.mergeGroupIntegrationId) {
      setGroupIntegrationId(pollingJobs.mergeGroupIntegrationId)
    }
  }, [pollingJobs.mergeGroupIntegrationId, setGroupIntegrationId])

  return teamId ? (
    <Modal show={show && !!recentIntegration} onClose={closeSuccessModal}>
      <div className="flex flex-col items-center">
        <Icon
          name="check"
          color="text-white"
          size={40}
          className="rounded-full bg-primary p-2 mb-5"
        />
        <h2 className="subtitle-1 mb-4 text-center">
          {recentIntegration} syncs with Everlance once a day. Updates you make
          in {recentIntegration} will automatically update in Everlance.
        </h2>
        <p className="text-dark mb-5 text-center">
          Go to the Members Table to manage your members.
        </p>
        <Button
          type="submit"
          variant="primary"
          onClick={() => {
            closeSuccessModal()
            push(TeamMembersUrl)
          }}
          className="w-56"
        >
          View & manage Members
        </Button>
      </div>
    </Modal>
  ) : null
}

export default MergeSuccessModal
